import { useState, useEffect, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { ImageGalleryProps } from '../../types';

export function ImageGallery({
  images,
  alt,
  className = '',
  showThumbnails = true,
  thumbnailPosition = 'bottom',
  enableKeyboardNavigation = true
}: ImageGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enableKeyboardNavigation || images.length <= 1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        setCurrentImageIndex(prev => 
          prev === 0 ? images.length - 1 : prev - 1
        );
      } else if (event.key === 'ArrowRight') {
        setCurrentImageIndex(prev => 
          prev === images.length - 1 ? 0 : prev + 1
        );
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [images.length, enableKeyboardNavigation]);

  // Reset index and error when images change
  useEffect(() => {
    setCurrentImageIndex(0);
    setImgError(false);
  }, [images]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    setShowZoom(true);
  };

  const handleMouseLeave = () => {
    setShowZoom(false);
  };

  const [imgError, setImgError] = useState(false);

  if (images.length === 0 || imgError) {
    return (
      <div className={`aspect-square bg-gray-100 rounded-lg flex flex-col items-center justify-center gap-3 text-gray-400 ${className}`}>
        <ImageOff className="w-16 h-16" aria-hidden="true" />
        <span className="text-sm">No image available</span>
      </div>
    );
  }

  const layoutClasses = thumbnailPosition === 'right' 
    ? 'flex flex-row space-x-4' 
    : 'flex flex-col space-y-4';

  const safeIndex = Math.min(currentImageIndex, images.length - 1);
  const currentImage = images[safeIndex];
  const currentImageUrl = typeof currentImage === 'string'
    ? currentImage
    : currentImage.url;

  return (
    <div className={layoutClasses}>
      {/* Main Image */}
      <div className={`relative ${thumbnailPosition === 'right' ? 'flex-1' : ''}`}>
        <div
          ref={imageRef}
          className={`aspect-square overflow-hidden bg-gray-100 ${className} cursor-crosshair relative`}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <img
            src={currentImageUrl}
            alt={`${alt} ${safeIndex + 1}`}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
          />

          {/* Zoom Lens Indicator */}
          {showZoom && (
            <div
              className="absolute w-24 h-24 border-2 border-white shadow-lg pointer-events-none opacity-50 bg-white/20"
              style={{
                left: `${zoomPosition.x}%`,
                top: `${zoomPosition.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
        </div>

        {/* Zoomed Image Display */}
        {showZoom && (
          <div className="absolute left-full ml-4 top-0 w-96 h-96 border-2 border-gray-300 bg-white shadow-xl overflow-hidden z-50 hidden lg:block">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${currentImageUrl})`,
                backgroundSize: '250%',
                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {showThumbnails && images.length > 1 && (
        <div className={`${
          thumbnailPosition === 'right'
            ? 'flex flex-col space-y-2 w-20'
            : 'flex space-x-2 overflow-x-auto overflow-y-hidden'
        }`}>
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`flex-shrink-0 ${
                thumbnailPosition === 'right' ? 'aspect-square w-20' : 'w-16 h-16'
              } rounded-lg overflow-hidden border-2 transition-colors ${
                index === safeIndex
                  ? 'border-blue-600'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                  src={typeof image === 'string' ? image : image.url}
                  alt={`${alt} thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Indicators (for bottom thumbnails when space is limited) */}
      {!showThumbnails && images.length > 1 && (
        <div className="flex justify-center space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === safeIndex
                  ? 'bg-blue-600'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}